import dayjs from 'dayjs'
import logoBase64 from '@/assests/shakthiLogo'
import { api } from '@/services/api'

// The PDF description is sourced from the invoice's internal notes (so it
// matches what's shown on the view page), but the "(allocation UA028)"
// reference in those notes is an internal bookkeeping id — meaningless (and
// unnecessary to expose) to the generator/consumer reading the printed invoice.
export const stripAllocationRef = (text: string): string =>
  text.replace(/\s*\(allocation\s+[^)]*\)/gi, '').trim()

export interface InvoicePdfData {
  invoiceNumber: string
  issueDate: string
  dueDate: string
  customerId: string
  poNumber?: string
  clientName: string
  clientAddress: string[]
  clientGstNo?: string
  description: string
  hsnSacCode: string
  quantity: number
  ratePerUnit: number
  lineTotal: number
  cgstRate: number
  cgstAmount: number
  sgstRate: number
  sgstAmount: number
  igstRate?: number
  igstAmount?: number
  subtotal: number
  roundedOff: number
  discount: number
  totalAmount: number
  amountInWords: string
  // Company bank account shown in the "Bank Details" block — sourced from
  // Bank Master (/master/bank-accounts). Omit any field to hide that line.
  bankName?: string
  bankBranch?: string
  bankAccountNumber?: string
  bankIfscCode?: string
  bankSwiftCode?: string
}

export const generateInvoiceHTML = (data: InvoicePdfData): string => {
  const {
    invoiceNumber,
    issueDate,
    dueDate,
    customerId,
    poNumber,
    clientName,
    clientAddress,
    clientGstNo,
    description,
    hsnSacCode,
    quantity,
    ratePerUnit,
    lineTotal,
    cgstRate,
    cgstAmount,
    sgstRate,
    sgstAmount,
    igstRate,
    igstAmount,
    subtotal,
    roundedOff,
    discount,
    totalAmount,
    amountInWords,
    bankName,
    bankBranch,
    bankAccountNumber,
    bankIfscCode,
    bankSwiftCode,
  } = data

  const fmtDate = (d: string) => dayjs(d).format('DD-MM-YYYY')
  const fmtMoney = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const hasIgst = igstAmount && igstAmount > 0

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Invoice - ${invoiceNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @media print {
      body { background: white; }
      .page { box-shadow: none; margin: 0; padding: 15mm 15mm 15mm 15mm; }
    }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #000; background: #e8e8e8; }
    .page {
      width: 210mm; min-height: 297mm;
      margin: 10px auto; padding: 15mm 15mm 15mm 15mm;
      background: white; box-shadow: 0 0 10px rgba(0,0,0,0.2);
      position: relative;
    }
    .header {
      display: flex; justify-content: space-between; align-items: flex-start;
      margin-bottom: 30px;
    }
    .header-left img { height: 65px; width: auto; margin-bottom: 10px; }
    .header-right { text-align: right; }
    .invoice-title { font-size: 36px; font-weight: 300; color: #001A4D; letter-spacing: 2px; margin-bottom: 20px; }
    
    .info-grid {
      display: flex; justify-content: flex-end; margin-bottom: 30px;
    }
    .info-table {
      border-collapse: collapse; font-size: 11px;
    }
    .info-table td { padding: 4px 8px; }
    .info-table td:first-child { font-weight: bold; color: #555; text-align: right; }
    .info-table td:last-child { font-weight: bold; text-align: left; }

    .party-grid {
      display: flex; justify-content: space-between; margin-bottom: 30px;
    }
    .party-box { width: 45%; }
    .party-title {
      font-size: 13px; font-weight: bold; color: #fff; background: #001A4D;
      padding: 6px 12px; margin-bottom: 10px; border-radius: 4px 4px 0 0;
    }
    .party-content {
      font-size: 12px; line-height: 1.6; padding: 0 12px;
    }

    table.items-table {
      width: 100%; border-collapse: collapse; font-size: 11.5px; margin-bottom: 20px;
    }
    table.items-table th {
      background: #001A4D; color: #fff; padding: 10px 8px; text-align: center;
      border: 1px solid #001A4D; font-weight: bold;
    }
    table.items-table th:first-child { text-align: left; }
    table.items-table td {
      padding: 10px 8px; border: 1px solid #ddd; text-align: center;
    }
    table.items-table td:first-child { text-align: left; }
    table.items-table td:last-child { text-align: right; font-weight: bold; }
    
    .tax-row td { border-top: none; border-bottom: none; padding: 4px 8px; }
    .tax-row td:first-child { text-align: right; font-weight: bold; color: #555; }

    .summary-grid {
      display: flex; justify-content: space-between; margin-top: 30px;
    }
    .other-details {
      width: 50%; font-size: 11px; line-height: 1.8;
    }
    .totals-box {
      width: 40%;
    }
    .totals-table {
      width: 100%; border-collapse: collapse; font-size: 12px;
    }
    .totals-table td { padding: 6px 12px; border-bottom: 1px solid #eee; }
    .totals-table td:first-child { font-weight: bold; color: #555; }
    .totals-table td:last-child { text-align: right; font-weight: bold; }
    .grand-total {
      background: #EFF4FF; color: #001A4D; font-size: 16px !important;
    }

    .amount-words {
      margin-top: 20px; font-size: 12px; font-weight: bold; color: #001A4D;
      background: #f8fafc; padding: 10px; border-left: 4px solid #001A4D;
    }

    .footer {
      position: absolute; bottom: 15mm; left: 15mm; right: 15mm;
      text-align: center; font-size: 10px; color: #666;
      border-top: 1px solid #ddd; padding-top: 10px; line-height: 1.6;
    }
    .footer-thanks {
      font-size: 13px; font-weight: bold; color: #001A4D; margin-bottom: 8px;
    }
  </style>
</head>
<body>
<div class="page">

  <div class="header">
    <div class="header-left">
      <img src="${logoBase64}" alt="Shakthi Power Logo" />
    </div>
    <div class="header-right">
      <div class="invoice-title">INVOICE</div>
      <table class="info-table" style="float: right;">
        <tr><td>Date:</td><td>${fmtDate(issueDate)}</td></tr>
        <tr><td>Invoice #:</td><td>${invoiceNumber}</td></tr>
        <tr><td>Customer ID:</td><td>${customerId}</td></tr>
        ${poNumber ? `<tr><td>Purchase Order #:</td><td>${poNumber}</td></tr>` : ''}
        <tr><td>Payment Due by:</td><td>${fmtDate(dueDate)}</td></tr>
      </table>
      <div style="clear: both;"></div>
    </div>
  </div>

  <div class="party-grid">
    <div class="party-box">
      <div class="party-title">Bill To:</div>
      <div class="party-content">
        <strong>${clientName}</strong><br>
        ${clientAddress.join('<br>')}<br>
        ${clientGstNo ? `<strong>GST No:</strong> ${clientGstNo}` : ''}
      </div>
    </div>
    <div class="party-box">
      <div class="party-title">Bank Details:</div>
      <div class="party-content" style="background: #f8fafc; padding: 12px; border: 1px dashed #cbd5e1;">
        ${bankName ? `<strong>${bankName}</strong><br>` : ''}
        ${bankAccountNumber ? `<strong>A/C No:</strong> ${bankAccountNumber}<br>` : ''}
        ${bankBranch ? `<strong>Branch:</strong> ${bankBranch}<br>` : ''}
        ${bankIfscCode ? `<strong>IFSC Code:</strong> ${bankIfscCode}<br>` : ''}
        ${bankSwiftCode ? `<strong>SWIFT Code:</strong> ${bankSwiftCode}` : ''}
        ${!bankName && !bankAccountNumber ? '<span style="color:#94a3b8;">No default bank account configured — set one in Bank Master.</span>' : ''}
      </div>
    </div>
  </div>

  <table class="items-table">
    <thead>
      <tr>
        <th>Description</th>
        <th>HSN / SAC Code</th>
        <th>Quantity in Units</th>
        <th>Rate Per Unit</th>
        <th>Line Total</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${description}</td>
        <td>${hsnSacCode}</td>
        <td>${quantity.toLocaleString('en-IN')}</td>
        <td>${ratePerUnit.toFixed(2)}</td>
        <td>${fmtMoney(lineTotal)}</td>
      </tr>
      <tr style="height: 40px;"><td colspan="5" style="border:1px solid #ddd; border-top:none; border-bottom:none;"></td></tr>
      
      ${!hasIgst ? `
      <tr class="tax-row">
        <td colspan="4">CGST ${cgstRate}%</td>
        <td>${fmtMoney(cgstAmount)}</td>
      </tr>
      <tr class="tax-row">
        <td colspan="4">SGST ${sgstRate}%</td>
        <td>${fmtMoney(sgstAmount)}</td>
      </tr>
      ` : `
      <tr class="tax-row">
        <td colspan="4">IGST ${igstRate}%</td>
        <td>${fmtMoney(igstAmount || 0)}</td>
      </tr>
      `}
      <tr style="height: 20px;"><td colspan="5" style="border:1px solid #ddd; border-top:none; border-bottom:1px solid #ddd;"></td></tr>
    </tbody>
  </table>

  <div class="summary-grid">
    <div class="other-details">
      <strong>Other Details</strong><br>
      PAN Number: ACLFS1178A<br>
      GST No: 33ACLFS1178A1ZD
    </div>
    <div class="totals-box">
      <table class="totals-table">
        <tr><td>Subtotal</td><td>${fmtMoney(subtotal)}</td></tr>
        ${discount > 0 ? `<tr><td>Discount</td><td>-${fmtMoney(discount)}</td></tr>` : ''}
        <tr><td>Rounded Off</td><td>${roundedOff > 0 ? '+' : ''}${roundedOff.toFixed(2)}</td></tr>
        <tr><td class="grand-total">Total</td><td class="grand-total">₹ ${fmtMoney(totalAmount)}</td></tr>
      </table>
    </div>
  </div>

  <div class="amount-words">
    Amount in Words: ${amountInWords} Only
  </div>

  <div class="footer">
    <div class="footer-thanks">Thank you for your business!</div>
    Should you have any enquiries concerning this invoice, please contact us.<br>
    Mantyralayam Tower, No 35 B 2nd Floor, Dr Rajendra Prasad Rd, Hasthinapuram, Chromepet, Chennai, 600044<br>
    E-mail: info@shakthipower.com &nbsp;&nbsp;|&nbsp;&nbsp; Web: www.shakthipower.com
  </div>

</div>
</body>
</html>`
}

export const downloadInvoiceAsPDF = async (
  data: InvoicePdfData,
  filename: string
): Promise<void> => {
  const html2pdf = (await import('html2pdf.js')).default

  const htmlContent = generateInvoiceHTML(data)

  const container = document.createElement('div')
  container.innerHTML = htmlContent
  container.style.position = 'absolute'
  container.style.left = '-9999px'
  container.style.top = '0'
  document.body.appendChild(container)

  const pageEl = container.querySelector('.page') as HTMLElement
  if (pageEl) {
    pageEl.style.boxShadow = 'none'
    pageEl.style.margin = '0'
  }

  await html2pdf()
    .set({
      margin: 0,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: 'avoid-all' },
    } as any)
    .from(pageEl || container)
    .save()

  document.body.removeChild(container)
}

export const printInvoicePDF = (htmlContent: string) => {
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.write(htmlContent)
    printWindow.document.close()
    printWindow.focus()
    setTimeout(() => {
      printWindow.print()
    }, 500)
  }
}

/**
 * Sends the invoice as a rich HTML email via the backend (Resend), with the
 * actual invoice PDF attached (rendered server-side via Puppeteer — real
 * headless Chromium, matching what Print shows). The backend resolves the
 * actual recipient from the consumer/generator's Contact Email — this
 * payload only supplies display content, not who it's sent to.
 * Returns the address it was actually sent to.
 */
export const emailInvoice = async (invoiceId: string, data: InvoicePdfData): Promise<string> => {
  const result = await api.emailInvoice(invoiceId, { ...data, htmlContent: generateInvoiceHTML(data) })
  return result.sentTo
}
